<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Mobile menu overlay -->
    <div 
      v-if="isMobileMenuOpen" 
      class="fixed inset-0 z-40 lg:hidden"
      @click="closeMobileMenu"
    >
      <div class="fixed inset-0 bg-black bg-opacity-50"></div>
    </div>

    <!-- Sidebar -->
    <aside 
      :class="[
        'fixed inset-y-0 left-0 z-50 w-64 bg-indigo-700 text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 shadow-2xl',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <!-- Mobile header inside sidebar -->
      <div class="flex items-center justify-between h-16 px-4 border-b border-indigo-600 lg:hidden">
        <h1 class="text-xl font-bold">biljetter.se</h1>
        <button @click="closeMobileMenu" class="text-white hover:text-gray-300 p-1">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="flex flex-col h-full">
        <!-- Desktop logo -->
        <div class="hidden lg:block p-4 border-b border-indigo-600">
          <h1 class="text-2xl font-bold">biljetter.se</h1>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-4 py-6 space-y-2">

          <template v-if="user">
            <!-- Organizer/Admin view: Events, Create, QR -->
            <template v-if="isOrganizerView">
              <router-link 
                to="/events" 
                class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
                :class="$route.path === '/events' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
                @click="closeMobileMenu"
              >
                <span class="mr-3">📋</span>
                Mina evenemang
              </router-link>
              <router-link 
                to="/events/create" 
                class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
                :class="$route.path === '/events/create' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
                @click="closeMobileMenu"
              >
                <span class="mr-3">➕</span>
                Skapa event
              </router-link>
              <router-link 
                to="/qr-scanner" 
                class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
                :class="$route.path === '/qr-scanner' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
                @click="closeMobileMenu"
              >
                <span class="mr-3">📱</span>
                QR‑scanner
              </router-link>
            </template>

            <!-- Buyer: only My Tickets -->
            <template v-else>
              <router-link 
                to="/tickets" 
                class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
                :class="$route.path === '/tickets' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
                @click="closeMobileMenu"
              >
                <span class="mr-3">🎫</span>
                Mina biljetter
              </router-link>
            </template>
            
            <router-link 
              v-if="user.role === 'admin'"
              to="/admin" 
              class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
              :class="$route.path === '/admin' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
              @click="closeMobileMenu"
            >
              <span class="mr-3">⚙️</span>
              Admin Dashboard
            </router-link>
            
            <router-link 
              v-if="user.role === 'admin'"
              to="/organiser/create" 
              class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
              :class="$route.path === '/organiser/create' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
              @click="closeMobileMenu"
            >
              <span class="mr-3">👥</span>
              Skapa Organisatör
            </router-link>
          </template>

          <template v-else>
            <router-link 
              to="/login" 
              class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
              :class="$route.path === '/login' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
              @click="closeMobileMenu"
            >
              <span class="mr-3">🔑</span>
              Logga in
            </router-link>
            
            <router-link 
              to="/register" 
              class="flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-600 hover:text-white transition-colors"
              :class="$route.path === '/register' ? 'bg-indigo-600 text-white' : 'text-indigo-100'"
              @click="closeMobileMenu"
            >
              <span class="mr-3">📝</span>
              Registrera
            </router-link>
          </template>
        </nav>

        <!-- User section -->
        <div class="p-4 border-t border-indigo-600">
          <template v-if="user">
            <div class="flex items-center mb-3">
              <div class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center mr-3">
                <span class="text-sm font-medium">{{ user.name.charAt(0).toUpperCase() }}</span>
              </div>
               <div class="flex-1 min-w-0">
                 <p class="text-sm font-medium text-white truncate">
                   {{ isOrganizerView && user.organizerName ? user.organizerName : user.name }}
                 </p>
                 <p class="text-xs text-indigo-200 truncate">{{ user.email }}</p>
                 <p class="mt-1">
                   <span v-if="isOrganizerView" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-200 text-green-800">Eventorganisatör</span>
                   <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">Biljettköpare</span>
                 </p>
               </div>
            </div>
            <!-- Dynamic Switch Role Button -->
            <button 
              v-if="user.role === 'buyer'"
              @click="switchToOrganizer" 
              class="w-full mb-2 flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-md transition-colors"
            >
              <span class="mr-3">🚀</span>
              Växla till organisatör
            </button>
            <button 
              v-else-if="user.role === 'organizer' || user.role === 'admin'"
              @click="switchToBuyer" 
              class="w-full mb-2 flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-md transition-colors"
            >
              <span class="mr-3">🛍️</span>
              Växla till biljettköpare
            </button>

            <button 
              @click="logout" 
              class="w-full flex items-center px-3 py-2 text-sm font-medium text-indigo-100 hover:bg-indigo-600 hover:text-white rounded-md transition-colors"
            >
              <span class="mr-3">🚪</span>
              Logga ut
            </button>
          </template>
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Mobile header -->
      <header class="lg:hidden bg-white shadow-sm border-b border-gray-200">
        <div class="flex items-center justify-between h-16 px-4">
          <button @click="openMobileMenu" class="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
          <h1 class="text-lg font-semibold text-gray-900">biljetter.se</h1>
          <div class="w-6"></div> <!-- Spacer for centering -->
        </div>
      </header>

      <!-- Main content area -->
      <main class="flex-1 overflow-y-auto bg-gray-50">
        <div class="p-4 lg:p-8">
          <router-view />
        </div>
      </main>
    </div>

    <!-- Organizer Name Modal -->
    <div v-if="showOrganizerModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Välj organisatörsnamn</h3>
        
        <!-- Show different message based on organizer name status -->
        <p class="text-sm text-gray-600 mb-4" v-if="user?.organizerName && user.organizerName !== user.name">
          Du har redan organisatörsnamnet "{{ user.organizerName }}". Du kan behålla det eller välja ett nytt.
        </p>
        <p class="text-sm text-gray-600 mb-4" v-else-if="user?.organizerName && user.organizerName === user.name">
          Du använder ditt vanliga namn "{{ user.name }}" som organisatörsnamn. Du kan behålla det eller välja ett nytt.
        </p>
        <p class="text-sm text-gray-600 mb-4" v-else>
          Du har inte valt något organisatörsnamn än. Du kan använda ditt vanliga namn eller välja ett nytt.
        </p>
        
        <div class="mb-4">
          <label for="modalOrganizerName" class="block text-sm font-medium text-gray-700 mb-2">
            Organisatörsnamn *
          </label>
          <input
            id="modalOrganizerName"
            v-model="modalOrganizerName"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Ditt organisatörsnamn"
            @keyup.enter="confirmOrganizerName"
          />
          <p class="mt-1 text-sm text-gray-500">
            Detta namn kommer att visas på din organisatörssida.
          </p>
        </div>

        <div class="flex space-x-3">
          <button
            @click="cancelOrganizerName"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Avbryt
          </button>
          <button
            @click="confirmOrganizerName"
            :disabled="!modalOrganizerName.trim()"
            class="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ user?.organizerName && user.organizerName !== user.name ? 'Uppdatera namn' : 'Bekräfta' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Switch account type modal (only buttons) -->
  <div v-if="showLogin" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black bg-opacity-40" @click="closeLoginModal"></div>
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 text-center">
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Växla kontotyp</h3>
      <p class="text-sm text-gray-600 mb-4" v-if="isOrganizerView">Du är inloggad som eventorganisatör. Växla till biljettköpare om du vill se dina biljetter.</p>
      <p class="text-sm text-gray-600 mb-4" v-else>Du är inloggad som biljettköpare. Växla till eventorganisatör för att skapa evenemang.</p>

      <!-- If organizer/admin: only show switch to buyer button -->
      <div v-if="isOrganizerView" class="space-y-3">
        <button @click="switchToBuyer" class="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">Växla till biljettköpare</button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();
const user = computed(() => store.state.user);
const isMobileMenuOpen = ref(false);
const showLogin = ref(false);

// Computed property for organizer view
const isOrganizerView = computed(() => !!(user.value && (user.value.isOrganizer || user.value.role === 'organizer' || user.value.role === 'admin')));

// Organizer name modal state
const showOrganizerModal = ref(false);
const modalOrganizerName = ref('');

const openMobileMenu = () => {
  isMobileMenuOpen.value = true;
  // Prevent body scroll when mobile menu is open
  document.body.style.overflow = 'hidden';
  // Add ESC key listener
  document.addEventListener('keydown', handleEscKey);
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
  // Restore body scroll when mobile menu is closed
  document.body.style.overflow = '';
  // Remove ESC key listener
  document.removeEventListener('keydown', handleEscKey);
};

const handleEscKey = (event) => {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    closeMobileMenu();
  }
};

const logout = async () => {
  try {
    await axios.post('/api/auth/logout', {}, { withCredentials: true });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    store.commit('logout');
    closeMobileMenu(); // Close mobile menu on logout
    router.push('/');
  }
};

//const openLoginModal = () => { showLogin.value = true; };
const closeLoginModal = () => { showLogin.value = false; };



const switchToOrganizer = async () => {
  console.log('Current user data:', user.value);
  console.log('Organizer name:', user.value.organizerName);
  console.log('Organizer slug:', user.value.organizerSlug);
  
  // Only show modal if user doesn't have an organizer name
  if (!user.value.organizerName) {
    modalOrganizerName.value = user.value.name || '';
    showOrganizerModal.value = true;
  } else {
    // User already has organizer name, upgrade directly
    try {
      const requestData = {
        organizerName: user.value.organizerName,
        organizerSlug: user.value.organizerSlug
      };
      
      console.log('Sending organizer upgrade request with existing data:', requestData);
      
      const response = await axios.post('/api/auth/upgrade-to-organizer', requestData, { withCredentials: true });
      store.commit('setUser', response.data.user);
      store.commit('setToken', response.data.user.token);
      closeMobileMenu();
      router.push('/events');
    } catch (err) {
      console.error('Error upgrading to organizer:', err);
      alert(err.response?.data?.message || 'Kunde inte uppgradera till organisatör.');
    }
  }
};

const switchToBuyer = async () => {
  try {
          const response = await axios.post('/api/auth/switch-to-buyer', {}, { withCredentials: true });
      store.commit('setUser', response.data.user);
      store.commit('setToken', response.data.user.token);
      closeMobileMenu(); // Close mobile menu when switching roles
      router.push('/tickets');
  } catch (err) {
    console.error('Error switching to buyer:', err);
    alert(err.response?.data?.message || 'Kunde inte växla till biljettköpare.');
  }
};

const confirmOrganizerName = async () => {
  if (!modalOrganizerName.value.trim()) return;
  
  try {
    const requestData = {
      organizerName: modalOrganizerName.value.trim()
    };
    
    // If user is keeping the same name, send the existing slug
    if (user.value.organizerName === modalOrganizerName.value.trim() && user.value.organizerSlug) {
      requestData.organizerSlug = user.value.organizerSlug;
    }
    
    console.log('Sending organizer upgrade request with data:', requestData);
    
    const response = await axios.post('/api/auth/upgrade-to-organizer', requestData, { withCredentials: true });
    store.commit('setUser', response.data.user);
    store.commit('setToken', response.data.user.token);
    showOrganizerModal.value = false;
    modalOrganizerName.value = '';
    closeMobileMenu(); // Close mobile menu when switching roles
    router.push('/events');
  } catch (err) {
    console.error('Error upgrading to organizer:', err);
    alert(err.response?.data?.message || 'Kunde inte uppgradera till organisatör.');
  }
};

const cancelOrganizerName = () => {
  showOrganizerModal.value = false;
  modalOrganizerName.value = '';
};


</script>
